import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, FormControlLabel, Grid, IconButton, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { referralApi } from '../services/api';
import { useShell } from '../components/Layout';
import PageHeader from '../components/common/PageHeader';
import StatCard from '../components/common/StatCard';
import EmptyState from '../components/common/EmptyState';
import { CardsSkeleton, ErrorState } from '../components/common/States';
import ProgramRules from '../components/ProgramRules';
import { apiError, formatDate, formatDateTimeMsk, formatMoney, pluralize } from '../utils/format';
import { isMember } from '../utils/access';
import { CLIENT_STATUS, COMMISSION_STATUS, kopecks, referralLink } from '../utils/referral';
import { tokens } from '../theme';

const StatusChip = ({ map, value }) => {
  const st = map[value] || { label: value, bg: tokens.primaryTint, fg: tokens.inkSoft };
  return <Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 600 }} />;
};

const TELEGRAM_HINT = 'Ваш аккаунт в Telegram: @username или ссылка t.me/username';
const PHONE_HINT = 'С кодом страны, например +7 900 123-45-67. Нужен для связи и сверки выплат';

// The application to join the program from an existing account. A turned-down application is sent again with
// the same form.
const ApplyCard = ({ program, initial, onApplied }) => {
  const { profile } = useShell();
  const [form, setForm] = useState({ name: initial?.name || '', contact: initial?.contact || '', phone: initial?.phone || '', accept: false });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  if (!program.active) {
    return <Alert severity="info">Партнёрская программа сейчас не принимает новые заявки.</Alert>;
  }
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const r = await referralApi.join({ name: form.name, contact: form.contact, phone: form.phone, accept_partner_terms: form.accept });
      onApplied(r.data);
    } catch (err) {
      setError(apiError(err));
    }
    setSaving(false);
  };
  return (
    <Card>
      <CardContent component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 640 }}>
        <Typography variant="h6">{initial ? 'Подать заявку снова' : 'Заявка в партнёрскую программу'}</Typography>
        <Typography color="text.secondary">
          После одобрения вы получите постоянную ссылку. Каждый, кто зарегистрируется по ней, закрепится за вами, и вы получите {program.percent}% от его первой оплаты.
        </Typography>
        <TextField label="Email" value={profile?.email || ''} disabled helperText="Заявка подаётся на почту вашего аккаунта" />
        <TextField required label="Имя" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} inputProps={{ maxLength: 120 }} />
        <TextField required label="Telegram" placeholder="@username" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
          helperText={TELEGRAM_HINT} inputProps={{ maxLength: 120 }} />
        <TextField required label="Телефон" placeholder="+7 900 123-45-67" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
          helperText={PHONE_HINT} inputProps={{ maxLength: 30 }} />
        <ProgramRules program={program} />
        <FormControlLabel control={<Checkbox checked={form.accept} onChange={(e) => setForm({ ...form, accept: e.target.checked })} />}
          label="Принимаю условия партнёрской программы" />
        {error && <Alert severity="error">{error}</Alert>}
        <Box>
          <Button type="submit" variant="contained"
            disabled={saving || !form.accept || form.name.trim().length < 2 || !form.contact.trim() || !form.phone.trim()}>
            {saving ? 'Отправка…' : 'Отправить заявку'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const PendingCard = ({ agent, program }) => (
  <Stack spacing={2.5}>
    <Alert severity="info">
      <b>Заявка на рассмотрении.</b> Администратор ответит на неё в ближайшее время; ссылка начнёт работать после одобрения. Переходы до одобрения не засчитываются.
    </Alert>
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>Ваша заявка</Typography>
        <Typography variant="body2">Имя: {agent.name}</Typography>
        <Typography variant="body2">Telegram: {agent.contact}</Typography>
        <Typography variant="body2">Телефон: {agent.phone}</Typography>
        <Typography variant="body2">Подана: {formatDate(agent.terms_accepted_at)}</Typography>
      </CardContent>
    </Card>
    <ProgramRules program={program} />
  </Stack>
);

// Name, Telegram and phone of an agent who is already in the program. Shown as plain text; the form
// opens only with «Редактировать», and the data can be changed once a day (the server keeps the count).
const ContactCard = ({ agent, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: agent.name, contact: agent.contact, phone: agent.phone });
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const waitUntil = agent.contact_editable_at ? new Date(agent.contact_editable_at) : null;
  const locked = Boolean(waitUntil && waitUntil > new Date());
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const open = () => { setForm({ name: agent.name, contact: agent.contact, phone: agent.phone }); setError(''); setSaved(false); setEditing(true); };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await referralApi.update(form);
      const r = await referralApi.me();
      onSaved(r.data);
      setEditing(false);
      setSaved(true);
    } catch (err) {
      setError(apiError(err));
    }
    setSaving(false);
  };
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Контакты</Typography>
          {!editing && <Button size="small" onClick={open} disabled={locked}>Редактировать</Button>}
        </Stack>
        {!agent.phone && <Alert severity="warning">Укажите телефон: он нужен для связи и сверки выплат.</Alert>}
        {!editing ? (
          <Stack spacing={0.5}>
            <Typography variant="body2">Имя: {agent.name}</Typography>
            <Typography variant="body2">Telegram: {agent.contact}</Typography>
            <Typography variant="body2">Телефон: {agent.phone || '—'}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
              {locked
                ? `Контакты можно менять раз в сутки. Следующее изменение возможно ${formatDateTimeMsk(agent.contact_editable_at)} (МСК).`
                : 'Контакты можно менять раз в сутки.'}
            </Typography>
          </Stack>
        ) : (
          <>
            <Alert severity="info">Менять контакты можно только раз в сутки. Проверьте данные перед сохранением.</Alert>
            <TextField required label="Имя" value={form.name} onChange={set('name')} inputProps={{ maxLength: 120 }} />
            <TextField required label="Telegram" placeholder="@username" value={form.contact} onChange={set('contact')} helperText={TELEGRAM_HINT} inputProps={{ maxLength: 120 }} />
            <TextField required label="Телефон" placeholder="+7 900 123-45-67" value={form.phone} onChange={set('phone')} helperText={PHONE_HINT} inputProps={{ maxLength: 30 }} />
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction="row" spacing={1}>
              <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</Button>
              <Button onClick={() => setEditing(false)} disabled={saving}>Отмена</Button>
            </Stack>
          </>
        )}
        {saved && !editing && <Alert severity="success">Сохранено</Alert>}
      </CardContent>
    </Card>
  );
};

const LinkCard = ({ agent, program }) => {
  const [copied, setCopied] = useState(false);
  const link = referralLink(agent.code);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Card sx={{ background: tokens.heroGradient }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Ваша партнёрская ссылка</Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
          <Typography sx={{ fontSize: { xs: '1.05rem', sm: '1.35rem' }, fontWeight: 700, overflowWrap: 'anywhere' }}>{link}</Typography>
          <Tooltip title={copied ? 'Скопировано' : 'Скопировать'}>
            <IconButton onClick={copy} aria-label="Скопировать ссылку" sx={{ bgcolor: tokens.paper }}>
              {copied ? <CheckRoundedIcon color="success" /> : <ContentCopyRoundedIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Код {agent.code} закреплён за вами и не меняется. Вставляйте ссылку в свои ролики и посты.
        </Typography>
        {agent.status === 'blocked' && (
          <Alert severity="error" sx={{ mt: 2 }}>Участие приостановлено{agent.blocked_reason ? `: ${agent.blocked_reason}` : ''}. Новые клиенты и начисления не засчитываются.</Alert>
        )}
        {!program.active && program.deadline && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Программа остановлена. Оплаты клиентов засчитываются до {formatDate(program.deadline)}.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

const SectionCard = ({ title, children, empty }) => (
  <Card>
    <CardContent>
      <Typography variant="h6" sx={{ mb: 1.5 }}>{title}</Typography>
      {empty ? <Typography color="text.secondary" variant="body2">{empty}</Typography> : children}
    </CardContent>
  </Card>
);

// /partner — the agent's screen: the link, results, contacts, clients and payouts.
const PartnerPage = () => {
  const { profile, reloadPartner } = useShell();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setError('');
    referralApi.me().then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (isMember(profile)) return <Navigate to="/dashboard" replace />;

  const header = <PageHeader overline="Партнёрам" title="Партнёрская программа" subtitle="Приводите клиентов по своей ссылке и получайте вознаграждение с их первой оплаты." />;
  if (error) return <>{header}<ErrorState message={error} onRetry={load} /></>;
  if (!data) return <>{header}<CardsSkeleton count={3} height={140} /></>;
  const applied = (d) => { setData(d); reloadPartner(); };
  if (!data.agent) return <>{header}<ApplyCard program={data.program} onApplied={applied} /></>;
  if (data.agent.status === 'pending') return <>{header}<PendingCard agent={data.agent} program={data.program} /></>;
  if (data.agent.status === 'rejected') {
    return (
      <>
        {header}
        <Stack spacing={2.5}>
          <Alert severity="error"><b>Заявка отклонена.</b>{data.agent.blocked_reason ? ` ${data.agent.blocked_reason}` : ''} Вы можете исправить данные и подать заявку снова.</Alert>
          <ApplyCard program={data.program} initial={data.agent} onApplied={applied} />
        </Stack>
      </>
    );
  }

  const { agent, stats, clients, commissions, payouts, program } = data;
  return (
    <>
      {header}
      <Stack spacing={2.5}>
        <LinkCard agent={agent} program={program} />
        <Grid container spacing={2}>
          {[
            { label: 'Переходы', value: stats.clicks, helper: 'По вашей ссылке, без повторов' },
            { label: 'Регистрации', value: stats.clients, helper: 'Закреплены за вами' },
            { label: 'Оплатили', value: stats.paid_clients, helper: stats.clients ? `${Math.round((stats.paid_clients / stats.clients) * 100)}% от регистраций` : '' },
            { label: 'Ожидает', value: kopecks(stats.hold_kopecks), helper: `Станет доступно через ${program.hold_days} дней после оплаты` },
            { label: 'К выплате', value: kopecks(stats.available_kopecks), helper: 'Выплачивает администратор' },
            { label: 'Выплачено', value: kopecks(stats.paid_kopecks), helper: `${payouts.length} ${pluralize(payouts.length, 'выплата', 'выплаты', 'выплат')}` },
          ].map((s) => (
            <Grid item xs={6} md={4} lg={2} key={s.label}>
              <StatCard label={s.label} value={s.value} helperText={s.helper} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}><ContactCard agent={agent} onSaved={setData} /></Grid>
          <Grid item xs={12} md={7}><ProgramRules program={program} /></Grid>
        </Grid>

        <SectionCard title="Клиенты" empty={clients.length ? null : 'Пока никто не зарегистрировался по вашей ссылке.'}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Клиент</TableCell><TableCell>Регистрация</TableCell><TableCell>Оплата</TableCell><TableCell>Статус</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {clients.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.email}</TableCell>
                    <TableCell>{formatDate(c.created_at)}</TableCell>
                    <TableCell>{c.paid ? 'Оплатил' : 'Пока нет'}</TableCell>
                    <TableCell><StatusChip map={CLIENT_STATUS} value={c.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>

        <SectionCard title="Начисления" empty={commissions.length ? null : 'Начисления появятся, когда ваши клиенты оплатят тариф.'}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Клиент</TableCell><TableCell>Дата оплаты</TableCell><TableCell align="right">Оплата</TableCell>
                  <TableCell align="right">Вам</TableCell><TableCell>Доступно с</TableCell><TableCell>Статус</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commissions.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.client_email}</TableCell>
                    <TableCell>{formatDate(c.paid_at)}</TableCell>
                    <TableCell align="right">{formatMoney(c.paid_rub)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{kopecks(c.amount_kopecks)}</TableCell>
                    <TableCell>{formatDate(c.available_at)}</TableCell>
                    <TableCell>
                      <StatusChip map={COMMISSION_STATUS} value={c.status} />
                      {c.cancel_reason && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.cancel_reason}</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>

        {payouts.length > 0 && (
          <SectionCard title="Выплаты">
            <Stack spacing={1}>
              {payouts.map((p) => (
                <Stack key={p.id} direction="row" justifyContent="space-between">
                  <Typography variant="body2">{formatDate(p.created_at)}{p.note ? ` · ${p.note}` : ''}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{kopecks(p.amount_kopecks)}</Typography>
                </Stack>
              ))}
            </Stack>
          </SectionCard>
        )}
        {!clients.length && !commissions.length && (
          <EmptyState title="Начните с канала" description="Возьмите готовые ролики в нашем Telegram-канале, добавьте свою ссылку и публикуйте." />
        )}
      </Stack>
    </>
  );
};

export default PartnerPage;
